import { useState, useRef, useCallback, useEffect } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import { Button, Input } from '@pantry/ui';
import { PlusIcon, LoaderIcon, SearchIcon } from 'lucide-react';
import { toast } from 'sonner';
import {
  useAddShoppingListItem,
  useUnifiedSearch,
  type ProductGroupSummary,
} from '@pantry/api-client';
import type { Product } from '@pantry/shared';
import { AnalyticsEvents } from '@pantry/analytics';
import { useAnalytics } from '@/hooks/useAnalytics';

interface AddItemFormProps {
  householdId: string;
  listId: string;
}

/**
 * Unified add-item form with groups-first search.
 *
 * Search results show three sections:
 * 1. Groups — sets productGroupId (e.g., "2% Milk" → any 2% milk)
 * 2. Products — sets productId (e.g., "Neilson 2% Milk 4L")
 * 3. Add as custom — sets customName (always last when search has text)
 */
export function AddItemForm({ householdId, listId }: AddItemFormProps) {
  const { t } = useLingui();
  const [value, setValue] = useState('');
  const [debouncedValue, setDebouncedValue] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const isSubmittingRef = useRef(false);

  const addItem = useAddShoppingListItem();
  const { track } = useAnalytics();

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), 300);
    return () => clearTimeout(timer);
  }, [value]);

  const { groups, products, isLoading } = useUnifiedSearch(householdId, debouncedValue);

  const groupResults = groups.slice(0, 5);
  const productResults = (products as Product[]).slice(0, 5);
  const hasResults = groupResults.length > 0 || productResults.length > 0;
  const totalItems = groupResults.length + productResults.length;

  const isDropdownVisible =
    showDropdown && value.length >= 2 && (isLoading || hasResults || value.trim().length > 0);

  const clearAndRefocus = useCallback(() => {
    setValue('');
    setDebouncedValue('');
    setShowDropdown(false);
    setHighlightedIndex(-1);
    inputRef.current?.focus();
  }, []);

  const submitGroup = async (group: ProductGroupSummary) => {
    try {
      await addItem.mutateAsync({
        householdId,
        listId,
        data: { productGroupId: group.id },
      });
      track(AnalyticsEvents.SHOPPING_LIST_ITEM_ADDED, { householdId, listId, type: 'group' });
      clearAndRefocus();
    } catch {
      toast.error(t`Failed to add item. Please try again.`);
    }
  };

  const submitProduct = async (product: Product) => {
    try {
      await addItem.mutateAsync({
        householdId,
        listId,
        data: { productId: product.id },
      });
      track(AnalyticsEvents.SHOPPING_LIST_ITEM_ADDED, { householdId, listId, type: 'product' });
      clearAndRefocus();
    } catch {
      toast.error(t`Failed to add item. Please try again.`);
    }
  };

  const submitFreeText = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    try {
      await addItem.mutateAsync({
        householdId,
        listId,
        data: { customName: trimmed },
      });
      track(AnalyticsEvents.SHOPPING_LIST_ITEM_ADDED, { householdId, listId, type: 'custom' });
      clearAndRefocus();
    } catch {
      toast.error(t`Failed to add item. Please try again.`);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    try {
      // If a result is highlighted, submit that; otherwise submit free text
      if (highlightedIndex >= 0 && highlightedIndex < groupResults.length) {
        await submitGroup(groupResults[highlightedIndex]);
      } else if (highlightedIndex >= groupResults.length && highlightedIndex < totalItems) {
        await submitProduct(productResults[highlightedIndex - groupResults.length]);
      } else {
        await submitFreeText(value);
      }
    } finally {
      isSubmittingRef.current = false;
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    setValue(next);
    setShowDropdown(true);
    setHighlightedIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isDropdownVisible) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((i) => Math.min(i + 1, totalItems - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((i) => Math.max(i - 1, -1));
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
      setHighlightedIndex(-1);
    }
  };

  return (
    <div className="relative">
      <form
        onSubmit={(e) => void handleSubmit(e)}
        className="flex gap-2"
        aria-label={t`Add item to list`}
      >
        <div className="relative flex-1">
          <SearchIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            ref={inputRef}
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              if (value.length >= 2) setShowDropdown(true);
            }}
            onBlur={() => setShowDropdown(false)}
            placeholder={t`Search groups, products, or type a name...`}
            className="pl-9"
            maxLength={200}
            disabled={addItem.isPending}
            autoComplete="off"
            role="combobox"
            aria-expanded={isDropdownVisible}
            aria-autocomplete="list"
            aria-controls="add-item-suggestions"
            aria-activedescendant={
              highlightedIndex >= 0 ? `add-item-option-${highlightedIndex}` : undefined
            }
          />
        </div>
        <Button
          type="submit"
          disabled={!value.trim() || addItem.isPending}
          aria-label={t`Add item`}
        >
          <PlusIcon className="h-4 w-4 mr-1" />
          <Trans context="action_button">Add</Trans>
        </Button>
      </form>

      {isDropdownVisible && (
        <div
          id="add-item-suggestions"
          role="listbox"
          className="absolute left-0 right-0 top-full mt-1 z-50 rounded-md border border-border bg-popover shadow-md overflow-hidden max-h-80 overflow-y-auto"
        >
          {isLoading ? (
            <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
              <LoaderIcon className="h-4 w-4 animate-spin" />
              {t({ message: 'Searching...', context: 'autocomplete' })}
            </div>
          ) : (
            <>
              {/* Groups section */}
              {groupResults.length > 0 && (
                <div className="py-1">
                  <p className="px-3 py-1 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    <Trans>Groups</Trans>
                  </p>
                  {groupResults.map((group, index) => (
                    <div
                      key={`group-${group.id}`}
                      id={`add-item-option-${index}`}
                      role="option"
                      aria-selected={highlightedIndex === index}
                      className={`flex items-center gap-3 px-3 py-2 cursor-pointer text-sm transition-colors ${
                        highlightedIndex === index
                          ? 'bg-accent text-accent-foreground'
                          : 'hover:bg-accent hover:text-accent-foreground'
                      }`}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => void submitGroup(group)}
                      onMouseEnter={() => setHighlightedIndex(index)}
                    >
                      <div className="flex h-8 w-8 items-center justify-center rounded-md bg-muted text-base flex-shrink-0">
                        {group.icon || '📦'}
                      </div>
                      <div className="min-w-0 flex-1">
                        <span className="block truncate font-medium">{group.name}</span>
                        <span className="block truncate text-xs text-muted-foreground">
                          {group.memberCount} {group.memberCount === 1 ? 'product' : 'products'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Products section */}
              {productResults.length > 0 && (
                <div className={groupResults.length > 0 ? 'border-t border-border py-1' : 'py-1'}>
                  <p className="px-3 py-1 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    <Trans>Products</Trans>
                  </p>
                  {productResults.map((product, index) => {
                    const flatIndex = groupResults.length + index;
                    return (
                      <div
                        key={`product-${product.id}`}
                        id={`add-item-option-${flatIndex}`}
                        role="option"
                        aria-selected={highlightedIndex === flatIndex}
                        className={`flex items-center gap-3 px-3 py-2 cursor-pointer text-sm transition-colors ${
                          highlightedIndex === flatIndex
                            ? 'bg-accent text-accent-foreground'
                            : 'hover:bg-accent hover:text-accent-foreground'
                        }`}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => void submitProduct(product)}
                        onMouseEnter={() => setHighlightedIndex(flatIndex)}
                      >
                        {product.imageUrl ? (
                          <img
                            src={product.imageUrl}
                            alt=""
                            className="h-8 w-8 rounded object-cover flex-shrink-0"
                          />
                        ) : (
                          <div className="h-8 w-8 rounded bg-muted flex-shrink-0" />
                        )}
                        <div className="min-w-0 flex-1">
                          <span className="block truncate font-medium">{product.name}</span>
                          {product.brand && (
                            <span className="block truncate text-xs text-muted-foreground">
                              {product.brand}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* No results message */}
              {!hasResults && value.trim().length >= 2 && (
                <div className="px-3 py-2 text-sm text-muted-foreground">
                  {t({ message: 'No groups or products match', context: 'autocomplete' })} &quot;
                  {value.trim()}&quot;
                </div>
              )}

              {/* Add as custom — always visible when text is entered */}
              {value.trim().length > 0 && (
                <div className={`${hasResults ? 'border-t border-border' : ''}`}>
                  <div
                    className="flex items-center gap-2 px-3 py-2.5 cursor-pointer text-sm text-primary font-medium hover:bg-accent transition-colors"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => void submitFreeText(value)}
                  >
                    <PlusIcon className="h-4 w-4" />
                    <span>
                      <Trans>Add &quot;{value.trim()}&quot; as custom item</Trans>
                    </span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
